import { Selection } from "@tiptap/pm/state";

import { appendBlock } from "@/components/doc/right-sidebar/create-blocks/utils/appendBlock";
import { getLatexMathParser } from "@/components/tiptap/latex-input/utils";
import { insertFileAttachment } from "@/components/tiptap/utils/insertFileAttachment";
import { insertImage } from "@/components/tiptap/utils/insertImage";
import { preserveMathCursorPosition } from "@/components/tiptap/utils/preserveMathCursorPosition";
import { documentScope } from "@/components/tiptap/utils/uploadScope";
import { insertNode } from "@/input/utils/editOperators";
import { cursorIsActive } from "@/models/CursorModel";
import { GraphNodeModel } from "@/models/nodes/GraphNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { nodeFromJson } from "@/models/nodes/nodeFromJson";
import { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";
import { afterDelayedFocus } from "@/utils/afterDelayedFocus";
import { trackEvent } from "@/utils/trackEvent";
import { VALID_IMAGE_TYPES } from "@/utils/validateImageFile";

import type { UploadScope } from "@/components/tiptap/utils/uploadScope";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { Editor } from "@tiptap/core";

/**
 * Shared editor mutations — the single source of truth for the block/heading/
 * alignment/font-size/image actions. Both the right-sidebar controls and the
 * mobile WebView bridge (@useMobileBridge) call these so the two surfaces can't
 * drift. Each takes the `DocumentModel` and reaches the TipTap editor + editor
 * state through it, matching how the sidebar already works.
 */

/** Block types the "Add block" control creates. */
export type CreateBlockType = "code" | "table" | "graph" | "horizontalRule";
/** Convertible text block types (paragraph + headings). */
export type BlockTextType = "paragraph" | "heading1" | "heading2" | "heading3";
/** Paragraph alignment; "math" toggles math indentation. */
export type EditorAlignment = "left" | "center" | "right" | "math";

/** Create a block from the "Add block" menu. Mirrors @CreateBlocksPanel. */
export function createBlock(doc: DocumentModel, type: CreateBlockType): void {
  const { tiptap, editorState } = doc.editor;
  if (!tiptap) return;
  switch (type) {
    case "code":
      if (editorState.isCodeBlock) return;
      if (editorState.isNonConvertableBlock) {
        appendBlock(doc, "code");
      } else {
        tiptap.chain().focus().appendCodeBlock().run();
      }
      return;
    case "table":
      appendBlock(doc, "table");
      return;
    case "graph": {
      if (editorState.isGraph) return;
      const graph = doc.transact(() => doc.create(GraphNodeModel, []), "insertGraph");
      appendBlock(doc, "graph", { graphId: graph.id });
      trackEvent("graph_insert", { source: "graph_button" });
      return;
    }
    case "horizontalRule":
      if (editorState.isHorizontalRule) {
        tiptap.chain().focus().deleteSelection().run();
      } else {
        appendBlock(doc, "horizontalRule");
      }
      return;
  }
}

/** Convert the current block to a paragraph or heading. Mirrors @TextHeadingDropdown. */
export function setBlockType(doc: DocumentModel, type: BlockTextType): void {
  const { tiptap, editorState } = doc.editor;
  if (!tiptap) return;

  if (type === "paragraph") {
    if (editorState.isNonConvertableBlock) {
      appendBlock(doc, "text");
      return;
    }
    preserveMathCursorPosition(doc, () => {
      if (editorState.isParagraph) return;
      tiptap.commands.setParagraph();
    });
    return;
  }

  const level = Number(type.replace("heading", "")) as 1 | 2 | 3;
  const isActive =
    (level === 1 && editorState.isH1) ||
    (level === 2 && editorState.isH2) ||
    (level === 3 && editorState.isH3);
  if (isActive) return;

  if (editorState.isNonConvertableBlock) {
    appendBlock(doc, type);
  } else {
    tiptap.chain().focus().setHeading({ level }).run();
  }
}

/** Set paragraph alignment, or toggle math indentation for "math". Mirrors @AlignmentButtons. */
export function setAlign(doc: DocumentModel, alignment: EditorAlignment): void {
  const { tiptap, editorState } = doc.editor;
  if (!tiptap) return;

  if (alignment === "math") {
    const { hasMathIndentation } = editorState;
    preserveMathCursorPosition(doc, () => {
      tiptap.chain().setMathIndentation(!hasMathIndentation).run();
    });
    return;
  }
  preserveMathCursorPosition(doc, () => {
    tiptap.chain().setAlign(alignment).run();
  });
}

/** Set the inline font size. Mirrors @InlineTextStyle. */
export function setFontSize(doc: DocumentModel, size: string): void {
  const { tiptap } = doc.editor;
  if (!tiptap) return;
  tiptap.chain().focus().setFontSize(size).run();
}

/**
 * Upload an image file and insert it as an image block. Mirrors the
 * @ImageUploadButton flow. `uploadScope` overrides the entity (document or
 * chat) the upload is attached to (defaults to the current doc); `select`
 * selects the inserted image.
 */
export function insertImageFile(
  doc: DocumentModel,
  file: File,
  uploadScope?: UploadScope,
  options?: { select?: boolean },
): void {
  const { tiptap } = doc.editor;
  if (!doc.id || !tiptap) return;

  const { view } = tiptap;
  insertImage(view, view.state.selection.head, file, uploadScope ?? documentScope(doc.id), options);
}

/**
 * Move the selection to the document end. Until the user first places the
 * caret, ProseMirror's selection sits at the document start; content added from
 * outside the editor (an "Add block" menu) should land at the end instead.
 */
export function moveSelectionToEnd(editor: Editor): void {
  const { state, view } = editor;
  view.dispatch(state.tr.setSelection(Selection.atEnd(state.doc)));
}

/**
 * Upload any file at the cursor: an image block for supported image types, a
 * file attachment otherwise. Mirrors the @DropImage flow.
 */
export function insertFile(doc: DocumentModel, file: File): void {
  const { tiptap } = doc.editor;
  if (!doc.id || !tiptap) return;

  if (VALID_IMAGE_TYPES.includes(file.type)) {
    insertImageFile(doc, file);
    return;
  }

  const { view } = tiptap;
  insertFileAttachment(view, view.state.selection.head, file, documentScope(doc.id));
}

/**
 * The first empty inline slot inside `nodes` (a fraction's numerator, a sqrt's
 * radicand, an integral's lower bound…), or undefined when every slot is filled.
 * A root's degree (its first parameter) is optional and normally hidden, so it
 * is never picked as the landing slot.
 */
function findFirstEmptyInline(nodes: NodeModel[]): InlineNodeModel | undefined {
  const isRootDegree = (node: NodeModel) =>
    node.parent?.is(OperatorNodeModel) && node.parent.operatorType === "root" && node.index === 0;

  for (const node of nodes) {
    const found = [node, ...node.descendants].find(
      (n): n is InlineNodeModel =>
        n.is(InlineNodeModel) && n.nodes.length === 0 && !isRootDegree(n),
    );
    if (found) return found;
  }
  return undefined;
}

/**
 * Parse a LaTeX snippet into math nodes and insert them: at the math cursor when
 * the caret is inside an expression, otherwise as a new inline expression at the
 * text caret. The cursor then lands in the first empty slot of the inserted nodes
 * (e.g. the numerator of `\frac{}{}`), or right after them when there is none.
 * Mirrors the `$…$` LaTeX autoconversion and the symbol palette's node insertion.
 *
 * Returns false when the LaTeX can't be parsed (or the parser hasn't loaded yet).
 */
export function insertMathFromLatex(doc: DocumentModel, latex: string): boolean {
  const parseMathLatex = getLatexMathParser();
  const { tiptap } = doc.editor;
  if (!parseMathLatex || !tiptap) return false;

  let nodeJson;
  try {
    nodeJson = parseMathLatex(latex);
  } catch {
    return false;
  }

  const { cursor } = doc;

  if (cursorIsActive(cursor)) {
    const inserted = doc.transact(
      () => {
        const inlineNode = nodeFromJson(doc, nodeJson);
        if (!inlineNode?.is(InlineNodeModel)) return [];

        const children = inlineNode.nodes;
        for (const child of children) {
          insertNode(cursor, child);
          cursor.moveAfterNode(child);
        }
        doc.removeNode(inlineNode);
        return children;
      },
      "editorActions:insertMathFromLatex",
      { forceNewUndoStack: true },
    );
    if (inserted.length === 0) return false;

    const target = findFirstEmptyInline(inserted);
    if (target) cursor.moveTo(target, 0);
    return true;
  }

  // Create and attach the expression within one transaction: a node that is
  // registered but not yet in the tree is an orphan at the transaction boundary.
  const inlineNode = doc.transact(
    () => {
      const node = nodeFromJson(doc, nodeJson);
      if (!node?.is(InlineNodeModel)) {
        if (node) doc.removeNode(node);
        return undefined;
      }
      tiptap.chain().insertInlineExpression(node).run();
      return node;
    },
    "editorActions:insertMathFromLatex",
    { forceNewUndoStack: true },
  );
  if (!inlineNode) return false;

  const target = findFirstEmptyInline(inlineNode.nodes);
  afterDelayedFocus(() => {
    if (target) {
      cursor.moveTo(target, 0, { forceFocus: true });
    } else {
      cursor.moveTo(inlineNode, inlineNode.nodes.length, { forceFocus: true });
    }
  });
  return true;
}
